import { toast, type ProgressHandle } from "@shuttlerock/mui-components";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSavingInProgress, useUploadState } from "../../contexts/UploadContext";
import i18n, { defaultNS } from "../../i18n";

const UPLOAD_PROGRESS_TOAST_KEY = "upload-progress";
/** Browser tests locate the progress toast by this id; keep it stable. */
const UPLOAD_PROGRESS_TEST_ID = "Uploading-GlobalProgressBar";

type UseGlobalUploadProgress = {
  totalFiles: number;
  /** Whole percent. The bar cannot show finer, and rounding here means the selector's
   *  deep-equality check re-renders the consumer only when the visible number changes. */
  totalProgress: number;
  uploadingInProgress: boolean;
  /** Files whose upload has failed. They stay in the uploader, so this is cumulative. */
  failedFiles: number;
};

/**
 * WARNING: Do not use this hook outside the `GlobalProgressBar` component as
 * using elsewhere will cause a re-render of that component for every little bit
 * uploaded on a file which we most certainly do not want!!!
 *
 * @internal
 */
export const useGlobalUploadProgress = (): UseGlobalUploadProgress =>
  useUploadState({
    selector: (state) => {
      const [bytesTotal, bytesUploaded, currentUploadsCount, failedCount] = Object.values(state)
        .map((uploader) => Object.values(uploader.files || {}))
        .flat()
        .reduce(
          (total, file) => {
            const [totalBytes, uploadedBytes, currentUploads, failed] = total;
            // A failed file never completes, so it would otherwise hold the batch open forever.
            const isFailed = !!file.error;
            return [
              totalBytes + (file.progress?.bytesTotal || 0),
              uploadedBytes + (file.progress?.bytesUploaded || 0),
              currentUploads + (!file.progress?.uploadComplete && !isFailed ? 1 : 0),
              failed + (isFailed ? 1 : 0),
            ];
          },
          [0, 0, 0, 0],
        );

      const totalProgress = Math.round((bytesUploaded / bytesTotal) * 100);

      const uploadingInProgress = currentUploadsCount > 0;

      return {
        totalFiles: currentUploadsCount,
        totalProgress,
        uploadingInProgress,
        failedFiles: failedCount,
      };
    },
  });

export const useHasUploadsInProgress = (): boolean => {
  const savingInProgress = useSavingInProgress();
  const hasCurrentUploads = useUploadState({
    selector: (state) => {
      return !!Object.values(state).filter(
        (uploader) => Object.values(uploader.currentUploads || {}).length,
      ).length;
    },
  });

  return savingInProgress || hasCurrentUploads;
};

/** One open progress toast and the failure count it started from. Null between batches. */
type Batch = {
  handle: ProgressHandle;
  failedAtStart: number;
};

/** What the upload state asks of the toast. Saving alone never opens one; it only holds one open. */
type Phase = "idle" | "uploading" | "saving";

const resolvePhase = (uploading: boolean, saving: boolean): Phase => {
  if (uploading) return "uploading";
  return saving ? "saving" : "idle";
};

/**
 * Headless controller for the upload progress toast. It renders nothing: it
 * drives `toast.progress` from the aggregate upload state, so the toast lives
 * in the one stack and cannot be dismissed while uploads or post-upload
 * persistence run. A batch opens on its first byte, holds through persistence,
 * and ends as a success or, if any file in the batch failed, a warning. Mount it
 * once per route that hosts an `UploadContextProvider`.
 */
export const GlobalProgressBar = (): null => {
  const { t } = useTranslation(defaultNS, { i18n });
  const { totalFiles, totalProgress, uploadingInProgress, failedFiles } = useGlobalUploadProgress();
  const savingInProgress = useSavingInProgress();

  const phase = resolvePhase(uploadingInProgress, savingInProgress);
  const message = t("globalProgress:remainingFiles", { count: Math.max(totalFiles, 1) });
  const value = uploadingInProgress ? totalProgress : 100;

  const batchRef = useRef<Batch | null>(null);

  // Reconciles the toast with the phase: the one transition between what is open and
  // what the state asks for. Open on the first byte, update while anything runs, settle
  // once everything has stopped.
  useEffect(() => {
    const batch = batchRef.current;
    if (!batch) {
      if (phase === "uploading") {
        batchRef.current = {
          handle: toast.progress(message, {
            key: UPLOAD_PROGRESS_TOAST_KEY,
            value,
            testId: UPLOAD_PROGRESS_TEST_ID,
          }),
          failedAtStart: failedFiles,
        };
      }
      return;
    }
    if (phase !== "idle") {
      batch.handle.update({ message, value });
      return;
    }
    batchRef.current = null;
    void (failedFiles > batch.failedAtStart
      ? batch.handle.fail(t("globalProgress:uploadFailed"), { severity: "warning" })
      : batch.handle.succeed(t("globalProgress:uploadComplete")));
  }, [phase, message, value, failedFiles, t]);

  // Mount-only: as the reconciler's cleanup this would close the toast on every update.
  useEffect(
    () => () => {
      batchRef.current?.handle.close();
      batchRef.current = null;
    },
    [],
  );

  return null;
};
